/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import React from "react";

import { useDispatch } from "react-redux";
import { useHistory } from "react-router";

import Pagination from "rc-pagination";

import {
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IColumn,
  IDetailsRowProps,
  SelectionMode,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";

import ChevronIcon from "Components/Common/ChevronIcon/ChevronIcon";
import { PaginationRow } from "Components/Common/Pagination/Pagination.styles";
import { detailListStyles, spinnerStyles } from "Components/Common/SharedListStyles.styles";
import { TenantTypesEnum } from "Constants/TenantTypes";
import {
  updateCurrentPage,
  updatePageSize,
  updateSelectedDistributionList,
} from "Dataflows/DistributionLists/DistributionListsAction";
import { IDistributionListResult } from "Interfaces/DistributionLists/IDistributionListResult";

import { IDistributionListsProps } from "./IDistributionListsProps";

const DistributionLists: React.FunctionComponent<IDistributionListsProps> = (
  props: IDistributionListsProps
) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { currentPage, data, filteredCount, isLoadingDLs, loadDLsError, pageSize, tenant } = props;

  const columns: IColumn[] = [
    {
      key: "distributionGroupDisplayName",
      name: "Name",
      ariaLabel: "Name",
      fieldName: "distributionGroupDisplayName",
      minWidth: 240,
      isResizable: true,
    },
    {
      key: "distributionListEmail",
      name: "DL Email",
      ariaLabel: "DL Email",
      fieldName: "distributionListEmail",
      minWidth: 240,
      isResizable: true,
      isSorted: false,
    },
    {
      key: "technologyCategories",
      name: "Award Category",
      ariaLabel: "Award Category",
      fieldName: "technologyCategories",
      minWidth: 240,
      isResizable: true,
    },
    {
      key: "distributionListOwner",
      name: "Owner",
      ariaLabel: "Owner",
      fieldName: "distributionListOwner",
      minWidth: 100,
      isResizable: true,
    },
  ];

  // Remove award category column for RD
  const rdColumns = columns.filter(function (obj) {
    return obj.key !== "technologyCategories";
  });

  const handleClick = (distributionListEmail: string): void => {
    dispatch(updateSelectedDistributionList(distributionListEmail));
    const emailAlias = distributionListEmail.split("@")[0];
    history.push(`/${tenant}/distributionLists/${emailAlias}`);
  };

  const onRenderRow = (props?: IDetailsRowProps, defaultRender?: any): any => {
    const selectedJoinRequest = props!.item as any;

    return defaultRender!({
      ...props,
      item: selectedJoinRequest,
      onClick: () => handleClick(selectedJoinRequest.distributionListEmail),
    });
  };

  if (isLoadingDLs) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  }

  if (loadDLsError && loadDLsError !== "") {
    return <p>{loadDLsError}</p>;
  }

  return (
    <>
      <DetailsList
        items={data}
        columns={tenant === TenantTypesEnum.MVP ? columns : rdColumns}
        selectionMode={SelectionMode.none}
        getKey={(item: IDistributionListResult): string => item.distributionListEmail}
        setKey="none"
        layoutMode={DetailsListLayoutMode.fixedColumns}
        isHeaderVisible={true}
        onRenderRow={onRenderRow}
        enterModalSelectionOnTouch={true}
        styles={detailListStyles}
      />
      <PaginationRow>
        <Pagination
          total={filteredCount}
          onChange={(page) => dispatch(updateCurrentPage(page))}
          showTitle={false}
          current={currentPage}
          prevIcon={ChevronIcon("ChevronLeft")}
          nextIcon={ChevronIcon("ChevronRight")}
          pageSize={pageSize}
          showSizeChanger
        />
        <Dropdown
          options={[
            { key: 16, text: "Show 16 results" },
            { key: 32, text: "Show 32 results" },
            { key: 48, text: "Show 48 results" },
          ]}
          onChange={(_e, op): void => {
            dispatch(updatePageSize(op?.key as number));
          }}
          selectedKey={pageSize}
          className="pagination-dropdown"
          title="Page Size Dropdown"
        />
      </PaginationRow>
    </>
  );
};

export default DistributionLists;
